<div class="ad-panel" *ngIf="assetId">
  <header class="ad-top">
    <div>
      <p class="ad-kicker">ASSET DETAILS</p>
    </div>
    <span class="ad-grade-pill">{{ gradeLabel }}</span>
    <button type="button" class="ad-close" (click)="closed.emit()" aria-label="Close">×</button>
  </header>

  <p class="ad-error" *ngIf="error">{{ error }}</p>
  <p class="ad-hint" *ngIf="loading">Loading…</p>

  <ng-container *ngIf="asset && !loading">
    <div class="ad-identity">
      <img class="ad-icon" [src]="iconPath" [alt]="typeLabel" width="40" height="40" />
      <div>
        <h2>{{ asset.label || typeLabel }}</h2>
        <p class="ad-code">{{ assetCode }}</p>
      </div>
    </div>

    <nav class="ad-tabs">
      <button type="button" [class.active]="tab === 'overview'" (click)="tab = 'overview'">Overview</button>
      <button type="button" [class.active]="tab === 'specification'" (click)="tab = 'specification'">Specification</button>
      <button type="button" [class.active]="tab === 'lifecycle'" (click)="tab = 'lifecycle'">Lifecycle</button>
      <button type="button" [class.active]="tab === 'documents'" (click)="tab = 'documents'">Documents</button>
      <button type="button" [class.active]="tab === 'history'" (click)="tab = 'history'">History</button>
    </nav>

    <div class="ad-body">
      <div class="ad-main">
        <section *ngIf="tab === 'overview'" class="ad-overview">
          <p>{{ typeLabel }} on this route at {{ formatChainage(asset.measure_m) }}
            <ng-container *ngIf="asset.measure_to_m">– {{ formatChainage(asset.measure_to_m) }}</ng-container>.
          </p>
          <p>Status <strong>{{ mapStatus }}</strong> · Condition <strong>{{ conditionLabel }}</strong></p>
          <p *ngIf="detail?.remaining_life_years != null">Remaining life {{ detail?.remaining_life_years }} years</p>
        </section>

        <section *ngIf="tab === 'specification'" class="ad-spec">
          <div class="ad-grid">
            <div *ngFor="let f of specFields">
              <span>{{ f.label }}<ng-container *ngIf="f.unit"> ({{ f.unit }})</ng-container></span>
              <ng-container *ngIf="editing && f.kind !== 'readonly'; else specRead">
                <select *ngIf="f.kind === 'select'" [(ngModel)]="edit[f.key]" [name]="'spec-'+f.key">
                  <option value="">—</option>
                  <option *ngFor="let o of f.options" [value]="o.id">{{ o.label }}</option>
                </select>
                <input *ngIf="f.kind === 'date'" type="date" [(ngModel)]="edit[f.key]" [name]="'spec-'+f.key" />
                <input *ngIf="f.kind === 'number'" type="number" step="0.01" [(ngModel)]="edit[f.key]" [name]="'spec-'+f.key" />
                <input
                  *ngIf="f.kind === 'text'"
                  [(ngModel)]="edit[f.key]"
                  [name]="'spec-'+f.key"
                  [placeholder]="f.key === 'start_chainage' || f.key === 'end_chainage' ? '0+000' : ''"
                />
              </ng-container>
              <ng-template #specRead>
                <strong>{{ specValue(f) }}</strong>
              </ng-template>
            </div>
            <div>
              <span>Status</span>
              <span class="ad-badge ok">{{ mapStatus }}</span>
            </div>
          </div>
        </section>

        <section *ngIf="tab === 'lifecycle'" class="ad-life">
          <div class="ad-stepper">
            <button
              type="button"
              *ngFor="let s of stages; let i = index"
              class="ad-step"
              [class.done]="i < stageIndex(asset.lifecycle_status)"
              [class.current]="s.id === asset.lifecycle_status || (editing && s.id === edit['lifecycle_status'])"
              (click)="editing && setLifecycle(s.id)"
            >
              <i>{{ i + 1 }}</i>
              <span>{{ s.label }}</span>
            </button>
          </div>
          <div class="ad-grid">
            <div>
              <span>Installation date</span>
              <input *ngIf="editing" type="date" [(ngModel)]="edit['installation_date']" name="linstall" />
              <strong *ngIf="!editing">{{ formatDate(asset.installation_date) }}</strong>
            </div>
            <div>
              <span>Commissioning date</span>
              <input *ngIf="editing" type="date" [(ngModel)]="edit['commissioning_date']" name="lcomm" />
              <strong *ngIf="!editing">{{ formatDate(asset.commissioning_date) }}</strong>
            </div>
            <div>
              <span>Last inspection</span>
              <input *ngIf="editing" type="date" [(ngModel)]="edit['last_inspection_date']" name="llast" />
              <strong *ngIf="!editing">{{ formatDate(asset.last_inspection_date) }}</strong>
            </div>
            <div>
              <span>Next inspection</span>
              <input *ngIf="editing" type="date" [(ngModel)]="edit['next_inspection_date']" name="lnext" />
              <strong *ngIf="!editing">{{ formatDate(asset.next_inspection_date) }}</strong>
            </div>
            <div>
              <span>Expected life</span>
              <ng-container *ngIf="editing"><input type="number" step="0.1" [(ngModel)]="edit['expected_life_years']" name="lexp" /> years</ng-container>
              <strong *ngIf="!editing">{{ asset.expected_life_years != null ? asset.expected_life_years + ' years' : '—' }}</strong>
            </div>
            <div>
              <span>Remaining life</span>
              <strong>{{ detail?.remaining_life_years != null ? detail?.remaining_life_years + ' years' : '—' }}</strong>
            </div>
            <div>
              <span>Replacement date</span>
              <input *ngIf="editing" type="date" [(ngModel)]="edit['replacement_date']" name="lrep" />
              <strong *ngIf="!editing">{{ formatDate(asset.replacement_date) }}</strong>
            </div>
            <div>
              <span>Decommission date</span>
              <input *ngIf="editing" type="date" [(ngModel)]="edit['decommission_date']" name="ldec" />
              <strong *ngIf="!editing">{{ formatDate(asset.decommission_date) }}</strong>
            </div>
          </div>
          <h4>Maintenance history</h4>
          <ul class="ad-maint" *ngIf="detail?.maintenance?.length; else noMaint">
            <li *ngFor="let m of detail?.maintenance">
              <strong>{{ m.work_type }}</strong>
              <span>{{ formatDate(m.work_date) }}</span>
              <small *ngIf="m.contractor">{{ m.contractor }}</small>
            </li>
          </ul>
          <ng-template #noMaint><p class="ad-hint">No maintenance records linked to this asset.</p></ng-template>
        </section>

        <section *ngIf="tab === 'documents'">
          <ul class="ad-docs">
            <li *ngFor="let d of detail?.documents">
              <span>{{ d.file_name || d.doc_type }}</span>
              <small>{{ d.doc_type }} · {{ formatBytes(d.byte_size) }}</small>
              <a *ngIf="d.url" [href]="d.url" target="_blank" rel="noopener">Download</a>
            </li>
          </ul>
          <p class="ad-hint" *ngIf="!detail?.documents?.length">No documents yet.</p>
          <button type="button" class="ad-btn secondary" (click)="pickFile()">Upload file</button>
        </section>

        <section *ngIf="tab === 'history'">
          <p>Created {{ formatDate(asset.created_at) }}</p>
          <p *ngIf="asset.decommission_date">Decommissioned {{ formatDate(asset.decommission_date) }}</p>
          <ul class="ad-maint" *ngIf="detail?.maintenance?.length">
            <li *ngFor="let m of detail?.maintenance">
              <strong>{{ m.work_type }}</strong> · {{ formatDate(m.work_date) }}
            </li>
          </ul>
        </section>
      </div>

      <aside class="ad-side">
        <section>
          <h4>Images ({{ images.length }})</h4>
          <img *ngIf="featuredImage?.url" class="ad-hero" [src]="featuredImage?.url" [alt]="featuredImage?.caption || 'Asset photo'" />
          <p class="ad-hint" *ngIf="!images.length">No photographs.</p>
          <div class="ad-thumbs" *ngIf="images.length">
            <button type="button" *ngFor="let img of images; let i = index" (click)="featuredImageIndex = i">
              <img [src]="img.url" [alt]="img.file_name" />
            </button>
          </div>
        </section>
        <section>
          <h4>Documents ({{ files.length }})</h4>
          <ul class="ad-docs">
            <li *ngFor="let d of files">
              <span>{{ d.file_name || d.doc_type }}</span>
              <small>{{ formatBytes(d.byte_size) }}</small>
              <a *ngIf="d.url" [href]="d.url" target="_blank" rel="noopener">↓</a>
            </li>
          </ul>
        </section>
        <section>
          <h4>Linked Assets ({{ detail?.links?.length || 0 }})</h4>
          <ul class="ad-links">
            <li *ngFor="let l of detail?.links">
              <span>{{ l.other?.label || ('#' + (l.other?.id || l.to_id)) }}</span>
              <button type="button" class="ad-eye" (click)="openOther(l.other?.kind, l.other?.id)" title="View">View</button>
            </li>
          </ul>
          <div class="ad-add-link" *ngIf="addLinkOpen">
            <select [(ngModel)]="linkKind" name="linkKind">
              <option value="event">Highway asset</option>
              <option value="utility">Utility</option>
            </select>
            <input type="number" [(ngModel)]="linkId" name="linkId" placeholder="Asset ID" />
            <select [(ngModel)]="linkRelation" name="linkRel">
              <option *ngFor="let r of relations" [value]="r.id">{{ r.label }}</option>
            </select>
            <button type="button" class="ad-btn primary" (click)="addLink()">Link</button>
          </div>
        </section>
      </aside>
    </div>

    <footer class="ad-actions">
      <button type="button" class="ad-btn primary" *ngIf="!editing" (click)="startEdit()">Edit</button>
      <button type="button" class="ad-btn primary" *ngIf="editing" [disabled]="saving" (click)="save()">{{ saving ? 'Saving…' : 'Save' }}</button>
      <button type="button" class="ad-btn secondary" *ngIf="editing" (click)="cancelEdit()">Cancel</button>
      <button type="button" class="ad-btn secondary" (click)="pickPhoto()">Add Photo</button>
      <div class="ad-more">
        <button type="button" class="ad-btn secondary" (click)="moreOpen = !moreOpen">More</button>
        <div class="ad-menu" *ngIf="moreOpen">
          <button type="button" (click)="addLinkOpen = true; moreOpen = false">Add linked asset</button>
          <button type="button" (click)="pickFile()">Upload document</button>
          <button type="button" (click)="retire()">Retire / decommission</button>
        </div>
      </div>
    </footer>
  </ng-container>

  <input #photoInput type="file" accept="image/*" hidden (change)="onPhotoSelected($event)" />
  <input #fileInput type="file" hidden (change)="onFileSelected($event)" />
</div>
